import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideFileUp, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { CompanyService } from '../../../../../services/company.service';
import { LoggerService } from '../../../../../services/logger.service';
import {
  DesignTemplate,
  DesignTemplates,
  VoucherDesign,
  VoucherDesignService,
  VoucherType,
  designErrorMessage,
} from '../../../../../services/voucher-design.service';
import { takeSelectedFile } from '../../../../../utils/image-file.utils';
import { validateDesignFile } from '../../../../../utils/design-file.utils';
import {
  BlankPage,
  BlankPageFormComponent,
  DEFAULT_BLANK_PAGE,
} from './blank-page-form/blank-page-form.component';
import { TemplatePickerComponent } from './template-picker/template-picker.component';

type Source = 'template' | 'upload' | 'blank';

/**
 * A new design from one of three sources: a bundled template, an uploaded file or a
 * blank page. The voucher type and an optional name apply to all three.
 */
@Component({
  selector: 'app-new-design-dialog',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmDialogImports,
    HlmInputImports,
    HlmLabelImports,
    HlmSpinnerImports,
    HlmTabsImports,
    HlmTooltipImports,
    HlmTypographyImports,
    BlankPageFormComponent,
    TemplatePickerComponent,
  ],
  providers: [provideIcons({ lucideFileUp, lucideTriangleAlert })],
  templateUrl: './new-design-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewDesignDialogComponent {
  private readonly designService = inject(VoucherDesignService);
  private readonly companyService = inject(CompanyService);
  private readonly logger = inject(LoggerService);
  private readonly router = inject(Router);

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly open = input(false);
  /** The voucher type preselected on opening, e.g. from the group the user started in. */
  readonly initialType = input<VoucherType>('money');
  readonly created = output<VoucherDesign>();
  readonly close = output<void>();

  readonly source = signal<Source>('template');
  readonly voucherType = signal<VoucherType>('money');
  readonly name = signal('');
  readonly templates = signal<DesignTemplates | null>(null);
  readonly templatesError = signal<string | null>(null);
  readonly selectedTemplate = signal<DesignTemplate | null>(null);
  readonly blankPage = signal<BlankPage>(DEFAULT_BLANK_PAGE);
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);

  readonly productsEnabled = computed(
    () => !!this.companyService.company()?.voucher_products_enabled,
  );
  readonly blankValid = computed(() => {
    const p = this.blankPage();
    const ok = (v: number) => Number.isFinite(v) && v >= 50 && v <= 1050;
    return ok(p.width_mm) && ok(p.height_mm) && /^#[0-9A-Fa-f]{6}$/.test(p.background_color);
  });
  readonly canCreate = computed(() => {
    if (this.busy()) return false;
    switch (this.source()) {
      case 'template':
        return !!this.selectedTemplate();
      case 'blank':
        return this.blankValid();
      default:
        return false;
    }
  });

  constructor() {
    // Each opening starts fresh and fetches the templates (their previews depend on
    // the current branding).
    effect(() => {
      if (!this.open()) return;
      untracked(() => this.reset());
    });
  }

  private reset() {
    this.source.set('template');
    this.voucherType.set(this.initialType());
    this.name.set('');
    this.selectedTemplate.set(null);
    this.blankPage.set(DEFAULT_BLANK_PAGE);
    this.error.set(null);
    this.busy.set(false);
    this.loadTemplates();
  }

  loadTemplates() {
    this.templatesError.set(null);
    this.designService.templates().subscribe({
      next: (templates) => this.templates.set(templates),
      error: (error) => {
        this.logger.error('Vorlagen konnten nicht geladen werden:', error);
        this.templatesError.set('Die Vorlagen konnten nicht geladen werden.');
      },
    });
  }

  setType(type: VoucherType) {
    if (type === this.voucherType()) return;
    this.voucherType.set(type);
    this.selectedTemplate.set(null); // templates are per type
  }

  onName(event: Event) {
    this.name.set((event.target as HTMLInputElement).value);
  }

  onDialogState(state: 'open' | 'closed') {
    if (state === 'closed' && this.open()) this.close.emit();
  }

  /** To the logo and banner uploads on the "Marke & Branding" tab; the dialog closes. */
  goToBranding() {
    this.close.emit();
    void this.router.navigate(['/settings'], {
      queryParams: { tab: 'brand' },
      fragment: 'section-logo',
    });
  }

  openPicker() {
    this.fileInput()?.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const file = takeSelectedFile(event);
    if (!file) return;
    const problem = validateDesignFile(file);
    if (problem) {
      this.error.set(problem);
      return;
    }
    this.run(this.designService.upload(file, this.voucherType(), this.name().trim() || undefined));
  }

  create() {
    if (!this.canCreate()) return;
    const name = this.name().trim() || null;
    if (this.source() === 'template') {
      const template = this.selectedTemplate()!;
      this.run(this.designService.createFromTemplate(template.key, this.voucherType(), name));
    } else {
      this.run(
        this.designService.createBlank({
          ...this.blankPage(),
          voucher_type: this.voucherType(),
          name,
        }),
      );
    }
  }

  private run(request: ReturnType<VoucherDesignService['createBlank']>) {
    this.busy.set(true);
    this.error.set(null);
    request.subscribe({
      next: (design) => {
        this.busy.set(false);
        this.created.emit(design);
      },
      error: (error) => {
        this.logger.error('Design konnte nicht angelegt werden:', error);
        this.busy.set(false);
        this.error.set(designErrorMessage(error, 'Das Design konnte nicht angelegt werden.'));
      },
    });
  }
}
